import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Check, Copy } from 'lucide-react';
import { Button } from '@/components/ui';
import { showToast } from '@/lib/toast';

export interface LicenseKeyRevealDialogProps {
  licenseKey: string | null;
  onClose: () => void;
}

/** Shown exactly once, right after a license key is issued or regenerated — the
 * raw key is never returned by any other API call, so this is the only chance
 * to copy it. */
export function LicenseKeyRevealDialog({ licenseKey, onClose }: LicenseKeyRevealDialogProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!licenseKey) return;
    await navigator.clipboard.writeText(licenseKey);
    setCopied(true);
    showToast('License key copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <AnimatePresence>
      {licenseKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="alertdialog" aria-modal="true">
          <motion.div
            className="absolute inset-0 bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="relative w-full max-w-md rounded-lg border border-border-subtle bg-surface-0 p-5 shadow-[var(--shadow-modal)]"
          >
            <h2 className="text-sm font-semibold text-text-primary">License key generated</h2>
            <p className="mt-1 text-xs text-state-warning">
              Copy this now — it will not be shown again.
            </p>
            <div className="mt-3 flex items-center gap-2 rounded-md border border-border-default bg-surface-1 px-3 py-2">
              <code className="flex-1 break-all font-mono text-xs text-text-primary">{licenseKey}</code>
              <button
                onClick={handleCopy}
                aria-label="Copy license key"
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-sm text-text-secondary transition-colors hover:bg-surface-2 hover:text-text-primary"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-state-success" /> : <Copy className="h-3.5 w-3.5" />}
              </button>
            </div>
            <div className="mt-5 flex justify-end">
              <Button variant="primary" size="sm" onClick={onClose}>
                Done
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
